// src/hooks/queries/useLessonsCatalog.ts
// Single cached fetch of the entire "lessons" collection.
// Re-used by useLessons, useWeekUnlock, useWeekQuestionnaireButton, WeeklyProgress.

import { useQuery } from "@tanstack/react-query";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../context/AuthContext";
import type { RecoveryLesson } from "../../types/recovery";

type LessonDoc = Omit<RecoveryLesson, "id">;

async function fetchLessonsCatalog(): Promise<RecoveryLesson[]> {
  const q = query(collection(db, "lessons"), orderBy("week"), orderBy("order"));
  const snap = await getDocs(q);

  const lessons: RecoveryLesson[] = [];
  snap.forEach((d) => {
    const data = d.data() as LessonDoc;
    if (data.enabled === false) return;
    lessons.push({ id: d.id, ...data });
  });
  return lessons;
}

/**
 * Cached query for the lessons catalog.
 * staleTime = Infinity because lesson content never changes mid-session.
 */
export function useLessonsCatalog() {
  const { user, loading } = useAuth();

  return useQuery({
    queryKey: ["lessons-catalog"],
    queryFn: fetchLessonsCatalog,
    staleTime: Infinity,
    enabled: !!user && !loading,
  });
}
